import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { DEFAULT_CLIDS, PLAYGAMA_CLIDS } from '@/constants/general'
import { CLIENT_QUERY, SSR_QUERY } from '@/router/query-params'
import type { EffectiveType } from '@/types'
import {
    getLandingClidFromCookies,
    getSessionCookie,
    setSessionCookie,
    updateLandingClidCookie,
} from '@/utils/cookie-parser'
import { isDesktopSafari, isDeviceIOS } from '@/utils/device'
import { getUTCYearWeek, idleCallbackPromise } from '@/utils/helpers'
import { composeLandingAttributes, parseLandingAttributes } from '@/utils/landing-attributes'
import { buildSessionCookieValue, getUtmHashFromQuery, parseSessionCookieValue } from '@/utils/session'

import { useAppStore } from './app-store'

const realUseClientAppStore = defineStore('clientApp', () => {
    const appStore = useAppStore()
    const router = useRouter()
    const route = useRoute()

    const auid = ref<string>()
    const referer = ref<string>()
    const userAgent = ref<string>()
    const effectiveType = ref<EffectiveType | undefined>()
    const userAcquisitionParams = ref<Record<string, string>>({})
    const isAuthBannerOpen = ref<boolean>(false)
    const pageLoadProgress = ref(false)
    const landingAttributesCookie = ref<string>()

    const auidTimestamp = computed(() => {
        if (auid.value) {
            const auidTimestampString = auid.value.split('.')[1]
            if (auidTimestampString) {
                return Number(auidTimestampString) * 1000
            }
            // before that date auids were without timestamp
            return Number(new Date('2025-11-13'))
        }
        return Date.now()
    })

    const cohort = computed(() => {
        return getUTCYearWeek(auidTimestamp.value)
    })

    const isPortalMobileOrganic = computed(() => {
        if (appStore.platform === 'desktop' || appStore.clid !== DEFAULT_CLIDS.direct || !appStore.isPortalEnv) {
            return false
        }
        const { medium, source } = userAcquisitionParams.value
        return (
            (medium === 'none' && source === 'direct') ||
            (medium === 'referral' && (source?.includes('google') || source?.includes('bing')))
        )
    })

    const experimentsString = computed(() => {
        return Object.entries(appStore.experiments)
            .map(([key, value]) => (value ? `${key}:${value.sample}` : false))
            .filter((x) => !!x)
            .join(', ')
    })

    const widgetId = computed(() => {
        const widgetIdFromQuery = appStore.initialQuery[CLIENT_QUERY.WIDGET_ID]
        if (typeof widgetIdFromQuery === 'string') {
            return widgetIdFromQuery
        }

        const { clid: landingClid, widgetId: landingWidgetId } = parseLandingAttributes(landingAttributesCookie.value)
        const isPlaygamaClid = Object.values(PLAYGAMA_CLIDS).includes(appStore.clid)
        if (appStore.clid === landingClid && !isPlaygamaClid && !appStore.isPartnerEmbed) {
            return landingWidgetId
        }

        return undefined
    })

    function setAuidInfo(token?: string) {
        auid.value = token
    }

    function setUserAgent(ua?: string) {
        userAgent.value = ua
    }

    function setReferer(value?: string) {
        referer.value = value
    }

    function setEffectiveType() {
        effectiveType.value =
            navigator.connection?.effectiveType ||
            `not_applicable${isDeviceIOS() || isDesktopSafari() ? '_ios' : '_other'}`
    }

    function setUserParams(details: { userAcquisitionParams: Record<string, string> }) {
        userAcquisitionParams.value = details.userAcquisitionParams
    }

    function updateLandingAttributes() {
        let currentCookieValue = getLandingClidFromCookies(document.cookie) || ''
        const currentCookieClid = parseLandingAttributes(currentCookieValue).clid || ''
        const queryClid = route.query[SSR_QUERY.CLID]

        if (typeof queryClid === 'string' && currentCookieClid !== queryClid && !appStore.isPartnerEmbed) {
            currentCookieValue = composeLandingAttributes(queryClid, (route.params.game as string) || 'Main_page')
            updateLandingClidCookie(currentCookieValue)
        }
        landingAttributesCookie.value = currentCookieValue
    }

    function initPageLoadProgressTracking() {
        router.beforeEach(async () => {
            pageLoadProgress.value = true
            // unblock thread
            await idleCallbackPromise()
        })
        router.afterEach(() => {
            pageLoadProgress.value = false
        })
    }

    let initialCall = true
    function getSessionId(): string {
        if (typeof window === 'undefined') {
            return ''
        }

        const { timestamp: cookieTimestamp, utmHash: cookieUtmHash } = parseSessionCookieValue(getSessionCookie())
        const currentUtmHash = getUtmHashFromQuery(appStore.initialQuery || {})

        // refresh the cookie on access to prolongate TTL, if the tab is active
        const needExtendCookie = !document.hidden

        // force update the session if a new tab with utm opens (except child tabs and iframes)
        const needResetCookie =
            initialCall &&
            !document.referrer.startsWith(location.origin) &&
            currentUtmHash !== '' &&
            currentUtmHash !== cookieUtmHash

        let sessionTimestamp = cookieTimestamp || Date.now().toString()

        if (needResetCookie) {
            sessionTimestamp = Date.now().toString()
        }

        if (needResetCookie || needExtendCookie) {
            setSessionCookie(buildSessionCookieValue(sessionTimestamp, currentUtmHash))
        }

        initialCall = false

        return `${sessionTimestamp}.${auid.value}`
    }

    return {
        auid,
        auidTimestamp,
        cohort,
        referer,
        userAgent,
        effectiveType,
        userAcquisitionParams,
        widgetId,
        isPortalMobileOrganic,
        experimentsString,
        isAuthBannerOpen,
        pageLoadProgress,
        setAuidInfo,
        setUserAgent,
        setReferer,
        setEffectiveType,
        setUserParams,
        updateLandingAttributes,
        initPageLoadProgressTracking,
        getSessionId,
    }
})

type ClientAppStore = ReturnType<typeof realUseClientAppStore>

export const useClientAppStore = (): ClientAppStore => {
    if (typeof window === 'undefined') {
        return new Proxy({} as Partial<ClientAppStore>, {
            get(target, prop: keyof ClientAppStore) {
                if (prop in target) {
                    return target[prop]
                }
                throw new Error(`Cannot read from clientAppStore on the SSR`)
            },
        }) as ClientAppStore
    }
    return realUseClientAppStore()
}
